import { Collection } from 'core/model/Collection';
import type { WithOptional } from '@kentik/ui-shared/types/util';
import type { PermissionGroup } from '@kentik/ui-shared/dictionary/types/rbacPermissions';
import type { RBACString } from '@kentik/ui-shared/rbac/constants';
import RbacPermModel, { type RbacPermAttributes } from './RbacPermModel';

type RbacPermissionsResponse = {
  groups: PermissionGroup[];
  hiddenPermissions?: RBACString[];
};

type RoleGrants = {
  permissions?: RBACString[] | null;
  labels?: Record<string, number[]> | null;
};

class RbacPermCollection extends Collection<RbacPermModel> {
  hiddenPermissions = new Set<RBACString>();

  get url() {
    return '/api/ui/rbac/permissions';
  }

  get model() {
    return RbacPermModel;
  }

  deserialize(data: RbacPermissionsResponse): WithOptional<RbacPermAttributes, 'id'>[] {
    const normalizedData: WithOptional<RbacPermAttributes, 'id'>[] = [];

    for (let i = 0; i < data.groups.length; i++) {
      const { name, permissions } = data.groups[i];

      for (let p = 0; p < permissions.length; p++) {
        const { permission, display, labelFilter } = permissions[p];
        normalizedData.push({ group: name, permission, display, labelFilter });
      }
    }

    this.hiddenPermissions = new Set(data.hiddenPermissions);

    return normalizedData;
  }

  // Reads every row, filtered out of view or not, and keeps the role's grants the company cannot see.
  rolePermissionsPayload({ permissions: heldPermissions, labels: heldLabels }: RoleGrants) {
    const keptHidden = (heldPermissions ?? []).filter((permission) => this.hiddenPermissions.has(permission));

    const permissions = this.unfiltered
      .filter((permModel) => permModel.get('isEnabled') === true)
      .map((permModel) => permModel.get('permission'))
      .concat(keptHidden);

    const labels: Record<string, number[]> = {};

    this.unfiltered.forEach((permModel) => {
      const labelMetadata = permModel.get('label_metadata');
      if (labelMetadata?.length) {
        labels[permModel.get('permission')] = labelMetadata;
      }
    });

    keptHidden.forEach((permission) => {
      const labelMetadata = heldLabels?.[permission];
      if (labelMetadata?.length) {
        labels[permission] = labelMetadata;
      }
    });

    return { permissions, labels };
  }
}

export default RbacPermCollection;
